'use client';

import React, { useState, useRef } from 'react';
import GenerateButton from '@/components/ui/GenerateButton';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';

const MAX_SIZE = process.env.NEXT_PUBLIC_MAX_FILE_SIZE ? parseInt(process.env.NEXT_PUBLIC_MAX_FILE_SIZE) : 1048576; // 1 MB in bytes (default)

interface HexDumpOptions {
    bytesPerLine: number;
    showAscii: boolean;
    upperCase: boolean;
}

function GenerateHexDumpPage() {
    const [input, setInput] = useState<string>('');
    const [hexDump, setHexDump] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [options, setOptions] = useState<HexDumpOptions>({
        bytesPerLine: 16,
        showAscii: true,
        upperCase: false,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        if (newValue.length <= MAX_SIZE) {
            setInput(newValue);
            setError(null);
        } else {
            setError(`Input exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
        }
    };

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
        }));
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > MAX_SIZE) {
                setError(`File size exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                if (content.length <= MAX_SIZE) {
                    setInput(content);
                    setError(null);
                } else {
                    setError(`File content exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
                }
            };
            reader.onerror = () => {
                setError('Error reading file. Please try again.');
            };
            reader.readAsText(file);
        }
    };

    const generateHexDump = () => {
        if (!input) {
            setError('Input is required.');
            return;
        }
        if (input.length > MAX_SIZE) {
            setError(`Input exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
            return;
        }
        if (options.bytesPerLine < 8 || options.bytesPerLine > 32) {
            setError('Bytes per line must be between 8 and 32.');
            return;
        }
        const bytes = new TextEncoder().encode(input);
        let result = '';
        let ascii = '';

        for (let i = 0; i < bytes.length; i++) {
            if (i % options.bytesPerLine === 0) {
                if (i > 0) {
                    result += options.showAscii ? `  ${ascii}\n` : '\n';
                    ascii = '';
                }
                result += i.toString(16).padStart(8, '0') + ': ';
            }

            const byte = bytes[i].toString(16).padStart(2, '0');
            result += options.upperCase ? byte.toUpperCase() : byte;
            result += ' ';

            ascii += bytes[i] >= 32 && bytes[i] <= 126 ? String.fromCharCode(bytes[i]) : '.';
        }

        if (ascii) {
            result += '   '.repeat(options.bytesPerLine - (bytes.length % options.bytesPerLine));
            result += options.showAscii ? `  ${ascii}` : '';
        }

        setHexDump(result);
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Hex Dump Generator</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Input and Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Input (max {MAX_SIZE / 1024} KB)</h2>
                        <textarea 
                            className="textarea textarea-bordered h-32"
                            value={input}
                            onChange={handleInputChange}
                            placeholder="Enter text or upload a file"
                            maxLength={MAX_SIZE}
                        />
                        <input 
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                        <button 
                            className="btn btn-secondary btn-sm mt-2"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Upload File
                        </button>
                        {error && (
                            <div className='alert alert-error mt-2'>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 shrink-0 stroke-current"
                                    fill="none"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label">
                            <span className="label-text">Bytes per line (8-32)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full ${options.bytesPerLine < 8 || options.bytesPerLine > 32 ? 'input-error' : ''}`}
                            name="bytesPerLine" 
                            min="8" 
                            max="32"
                            value={options.bytesPerLine} 
                            onChange={handleOptionChange} 
                        />
                        <label className="label cursor-pointer justify-start mt-2">
                            <input 
                                type="checkbox" 
                                className="checkbox checkbox-sm mr-2" 
                                name="showAscii" 
                                checked={options.showAscii} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Show ASCII</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="checkbox" 
                                className="checkbox checkbox-sm mr-2" 
                                name="upperCase" 
                                checked={options.upperCase} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Uppercase Hex</span>
                        </label>
                    </div>
                    <GenerateButton generatePasswords={generateHexDump} />
                </div>

                {/* Right Column - Generated Hex Dump */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Hex Dump</h2>
                    <AutoGrowingTextarea
                        value={hexDump}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateHexDumpPage;